/**
 * JobMatchExplainer — "Why did I get this job match?" breakdown overlay.
 */
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Building2,
  CheckCircle2,
  Code2,
  MapPin,
  TrendingUp,
  UserRound,
  X,
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

/* ── Types ── */
export interface MatchProfileContext {
  experienceYears?: number | null;
  industry?: string | null;
  currentRole?: string | null;
}

export interface MatchBreakdown {
  overall: number;
  factors: {
    experience: number;
    skills: number;
    industry: number;
    location?: number;
    seniority?: number;
  };
  matchedSkills: string[];
  missingSkills: string[];
  /** Pre-built reason bullets (may include __experience__:N tokens). */
  strongReasons?: string[];
  summary?: string;
}

interface JobMatchExplainerProps {
  visible: boolean;
  jobTitle: string;
  company: string;
  breakdown: MatchBreakdown;
  onClose: () => void;
  onOptimize?: () => void;
}

const FACTOR_META: Array<{
  key: keyof MatchBreakdown['factors'];
  Icon: React.FC<{ className?: string }>;
  labelKey: string;
  fallback: string;
}> = [
  { key: 'experience', Icon: Briefcase, labelKey: 'dashboard.matchExplain.factorExperience', fallback: 'Experience' },
  { key: 'skills', Icon: Code2, labelKey: 'dashboard.matchExplain.factorSkills', fallback: 'Skills' },
  { key: 'industry', Icon: Building2, labelKey: 'dashboard.matchExplain.factorIndustry', fallback: 'Industry' },
  { key: 'location', Icon: MapPin, labelKey: 'dashboard.matchExplain.factorLocation', fallback: 'Location' },
  { key: 'seniority', Icon: UserRound, labelKey: 'dashboard.matchExplain.factorSeniority', fallback: 'Seniority' },
];

function factorColor(score: number): string {
  if (score >= 85) return '#22c55e';
  if (score >= 65) return 'var(--ds-marketing-accent, #5F8568)';
  if (score >= 45) return '#f59e0b';
  return '#ef4444';
}

function formatStrongReason(
  reason: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (reason.startsWith('__experience__:')) {
    const years = reason.split(':')[1];
    return t('dashboard.matchExplain.reasonExperience', {
      count: Number(years),
      defaultValue: '{{count}} years of relevant experience',
    });
  }
  if (reason.startsWith('__industry__:')) {
    const industry = reason.split(':').slice(1).join(':');
    return t('dashboard.matchExplain.reasonIndustry', {
      industry,
      defaultValue: '{{industry}} experience',
    });
  }
  if (reason === '__leadership__') {
    return t('dashboard.matchExplain.reasonLeadership', 'Team leadership');
  }
  return reason;
}

const JobMatchExplainer: React.FC<JobMatchExplainerProps> = ({
  visible,
  jobTitle,
  company,
  breakdown,
  onClose,
  onOptimize,
}) => {
  const { t } = useTranslation();

  const strongItems = (breakdown.strongReasons?.length
    ? breakdown.strongReasons
    : breakdown.matchedSkills
  ).slice(0, 6);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="ds-match-explainer"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.2 }}
        >
          <div className="ds-match-explainer__header">
            <div className="flex-1 min-w-0">
              <p id="job-match-explainer-title" className="ds-match-explainer__modal-title">
                {t('dashboard.matchExplain.modalTitle', 'Why did I get this job match?')}
              </p>
              <p className="t-card-title truncate mt-0.5">{jobTitle}</p>
              <p className="t-meta">{company}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="ds-match-explainer__close touch-manipulation"
              aria-label={t('common.close', 'Close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="ds-match-explainer__score-hero">
            <span className="ds-match-explainer__score-label">
              {t('dashboard.matchExplain.matchLabel', 'Match')}
            </span>
            <span className="ds-match-explainer__score-value t-num">{breakdown.overall}%</span>
          </div>

          <div className="ds-match-explainer__why">
            <span className="ds-match-explainer__why-label">
              {t('dashboard.matchExplain.whyTitle', {
                score: breakdown.overall,
                defaultValue: `Why ${breakdown.overall}%?`,
              })}
            </span>
          </div>

          <div className="ds-match-explainer__factors">
            {FACTOR_META.map(({ key, Icon, labelKey, fallback }) => {
              const val = breakdown.factors[key];
              if (val == null) return null;
              const color = factorColor(val);
              return (
                <div key={key} className="ds-match-explainer__factor">
                  <div className="ds-match-explainer__factor-head">
                    <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} aria-hidden />
                    <span className="t-card-title flex-1">{t(labelKey, fallback)}</span>
                    <span className="t-card-title t-num" style={{ color }}>{val}%</span>
                  </div>
                  <div className="ds-progress" style={{ height: 5 }}>
                    <motion.div
                      className="ds-progress-fill"
                      style={{ background: color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${val}%` }}
                      transition={{ duration: 0.6, delay: 0.05 }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {strongItems.length > 0 && (
            <div className="ds-match-explainer__bucket">
              <p className="ds-match-explainer__bucket-title" style={{ color: '#22c55e' }}>
                {t('dashboard.matchExplain.strongBecause', "You're a strong match because:")}
              </p>
              <ul className="ds-match-explainer__bucket-list">
                {strongItems.map((s, i) => (
                  <li key={i} className="ds-match-explainer__bucket-item">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: '#22c55e' }} aria-hidden />
                    <span className="t-meta">{formatStrongReason(s, t)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {breakdown.missingSkills.length > 0 && (
            <div className="ds-match-explainer__bucket">
              <p className="ds-match-explainer__bucket-title" style={{ color: '#f59e0b' }}>
                {t('dashboard.matchExplain.missing', "You're missing:")}
              </p>
              <ul className="ds-match-explainer__bucket-list">
                {breakdown.missingSkills.slice(0, 6).map((s, i) => (
                  <li key={i} className="ds-match-explainer__bucket-item">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" style={{ color: '#f59e0b' }} aria-hidden />
                    <span className="t-meta">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {onOptimize && (
            <button
              type="button"
              onClick={onOptimize}
              className="ds-btn ds-btn-primary ds-btn-sm w-full mt-1 touch-manipulation"
            >
              {t('dashboard.matchExplain.ctaImprove', 'Improve my match')}
              <ArrowRight className="w-3.5 h-3.5 ml-1" aria-hidden />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default React.memo(JobMatchExplainer);
